import { useState, useEffect, useRef, useCallback } from 'react';
import { message } from 'antd';
import axios from 'axios';

/**
 * Maps transformation status strings to Ant Design Alert types
 *
 * @param status - Status string from backend
 * @returns Appropriate alert type for display
 */
export const getAlertType = (status?: string): 'success' | 'info' | 'warning' | 'error' => {
  if (!status) return 'info';

  const statusLower = status.toLowerCase();

  if (
    statusLower.includes('completed') ||
    statusLower.includes('success') ||
    statusLower.includes('erfolgreich')
  ) {
    return 'success';
  }

  if (
    statusLower.includes('failed') ||
    statusLower.includes('error') ||
    statusLower.includes('fehler')
  ) {
    return 'error';
  }

  if (
    statusLower.includes('processing') ||
    statusLower.includes('running') ||
    statusLower.includes('in_progress') ||
    statusLower.includes('started')
  ) {
    return 'info';
  }

  return 'info';
};

/**
 * Terminal statuses that should stop polling
 */
const TERMINAL_STATUSES = [
  'fertig',
  'erfolgreich',
  'abgeschlossen',
  'failed',
  'error',
  'success',
  'completed',
];

/**
 * Checks if a status is terminal (polling should stop)
 *
 * @param status - Status string to check
 * @returns True if status is terminal (completed, failed, error, etc.)
 */
const isTerminalStatus = (status?: string): boolean => {
  if (!status) return false;
  const statusLower = status.toLowerCase();
  return TERMINAL_STATUSES.some((s) => statusLower.includes(s));
};

/**
 * Transformation status data structure from backend
 */
interface TransformationStatusData {
  status?: string;
  message?: string;
  error?: string;
  client_id?: number;
  data_coll_name?: string;
  timestamp?: string;
  tables?: {
    data_coll_exists?: boolean;
    basis_upload_exists?: boolean;
    basis_exists?: boolean;
    data_coll_table_name?: string;
    basis_upload_table_name?: string;
    basis_table_name?: string;
  };
  calculation_status?: {
    calc_status?: string;
    calc_status_message?: string;
    calc_finished_at?: string | null;
    calc_duration?: number | null;
  };
  processing_details?: {
    calc_status?: string;
    calc_message?: string;
    error_details?: string;
    error_message?: string;
    state?: string;
    started_at?: string;
    failed_at?: string;
    completed_at?: string;
    status_message?: string;
    note?: string;
  };
  current_transformation?: {
    status?: string;
    message?: string;
    created_at?: string;
    updated_at?: string;
    is_current?: boolean;
  };
}

/**
 * Props for useTransformationStatus hook
 */
interface UseTransformationStatusProps {
  /** Name of the data collection to poll status for */
  dataCollName: string;
  /** Client authentication information */
  client: { id: string; token: string };
  /** Callback when status changes */
  onStatusChange?: (status: TransformationStatusData | null) => void;
  /** When transformation was triggered (used for stale detection). Null = no filtering */
  transformationTriggeredAt?: Date | null;
}

/**
 * useTransformationStatus Hook
 *
 * Fetches and polls transformation status with stale detection.
 *
 * **Stale Detection:**
 * - Compares backend `created_at` with `transformationTriggeredAt`
 * - If `created_at < triggeredAt` → STALE (ignored, continue polling)
 * - If `created_at >= triggeredAt` → FRESH (display it)
 * - If `transformationTriggeredAt` is null → all statuses are fresh
 *
 * **Polling:**
 * - Every 10s, stops on terminal status (completed/failed/error) AND fresh
 * - Continues polling while receiving stale statuses
 *
 * @returns Status data and control functions
 */
export const useTransformationStatus = ({
  dataCollName,
  client,
  onStatusChange,
  transformationTriggeredAt,
}: UseTransformationStatusProps) => {
  const [transformationStatus, setTransformationStatus] = useState<TransformationStatusData | null>(
    null
  );
  const [statusLoading, setStatusLoading] = useState(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const currentDataCollRef = useRef<string>(dataCollName);
  const triggeredAtRef = useRef<Date | null>(null);

  const updateStatus = useCallback(
    (newStatus: TransformationStatusData | null) => {
      setTransformationStatus(newStatus);
      if (onStatusChange) onStatusChange(newStatus);
    },
    [onStatusChange]
  );

  const isStaleStatus = useCallback((status: TransformationStatusData): boolean => {
    if (!triggeredAtRef.current) {
      console.log('No trigger timestamp set - treating all statuses as fresh');
      return false;
    }

    const createdAt = status.current_transformation?.created_at;
    if (!createdAt) {
      console.log('Status has no created_at - treating as fresh');
      return false;
    }

    try {
      // Ensure timestamp has 'Z' suffix for proper UTC parsing
      const isoTimestamp = createdAt.endsWith('Z') ? createdAt : `${createdAt}Z`;
      const statusTime = new Date(isoTimestamp);
      const triggerTime = triggeredAtRef.current;

      // Add 5-second tolerance buffer to account for client-server clock skew
      // This prevents fresh transformations from being permanently classified as stale
      // if the client clock is slightly ahead of the server clock
      const CLOCK_SKEW_TOLERANCE_MS = 5000;
      const triggerTimeWithBuffer = new Date(triggerTime.getTime() - CLOCK_SKEW_TOLERANCE_MS);

      // Compare timestamps with tolerance
      const isStale = statusTime < triggerTimeWithBuffer;

      console.log('Timestamp comparison:', {
        statusCreatedAt: isoTimestamp,
        statusTime: statusTime.toISOString(),
        triggerTime: triggerTime.toISOString(),
        triggerTimeWithBuffer: triggerTimeWithBuffer.toISOString(),
        statusTimeMs: statusTime.getTime(),
        triggerTimeMs: triggerTime.getTime(),
        difference: statusTime.getTime() - triggerTime.getTime(),
        isStale,
      });

      return isStale;
    } catch (error) {
      console.error('Error comparing timestamps:', error);
      return false;
    }
  }, []);

  const fetchTransformationStatus = useCallback(() => {
    if (!dataCollName || dataCollName.trim() === '') {
      return Promise.reject(new Error('No data collection name provided'));
    }

    return axios
      .post(
        `${process.env.REACT_APP_API_URL}/b2zero/${client.id}/get_data_coll_transformation_status`,
        { data_coll_name: dataCollName },
        {
          headers: {
            'Authorization': `Bearer ${client.token}`,
            'Content-Type': 'application/json',
          },
        }
      )
      .then((response) => {
        if (currentDataCollRef.current !== dataCollName) {
          console.log(`Ignoring status for ${dataCollName}, table changed`);
          return null;
        }
        return response.data;
      })
      .catch((error) => {
        throw new Error(error.message || 'Fehler beim Abrufen des Transformationsstatus');
      });
  }, [client.id, client.token, dataCollName]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();

    const poll = () => {
      setStatusLoading(true);
      fetchTransformationStatus()
        .then((statusRes) => {
          if (statusRes) {
            const isStale = isStaleStatus(statusRes);

            if (isStale) {
              console.log('Received stale status, continuing to poll...');
              setStatusLoading(false);
              return;
            }

            // Status is fresh, update UI
            updateStatus(statusRes);

            // Only stop polling if we have a terminal status AND it's fresh (not stale)
            if (isTerminalStatus(statusRes.status)) {
              console.log('Terminal status reached for current transformation, stopping poll');
              setStatusLoading(false);
              stopPolling();
            } else {
              setStatusLoading(false);
            }
          } else {
            // No status returned (e.g., data collection changed mid-request)
            setStatusLoading(false);
          }
        })
        .catch((error) => {
          const errorStatus = { error: error.message };
          updateStatus(errorStatus);
          setStatusLoading(false);
          stopPolling();
        });
    };

    poll();
    pollingRef.current = setInterval(poll, 10000);
  }, [fetchTransformationStatus, updateStatus, stopPolling, isStaleStatus]);

  const manualRefresh = useCallback(() => {
    if (!dataCollName || dataCollName.trim() === '') return;

    setStatusLoading(true);
    fetchTransformationStatus()
      .then((statusRes) => {
        if (statusRes) {
          // Check for stale status before updating
          const isStale = isStaleStatus(statusRes);
          if (isStale) {
            console.log('Manual refresh received stale status, not updating UI');
            // Optionally show a message to the user
            message.info('Status wird aktualisiert... Bitte warten.');
          } else {
            updateStatus(statusRes);
          }
        }
      })
      .catch((error) => {
        const errorStatus = { error: error.message };
        updateStatus(errorStatus);
      })
      .finally(() => {
        setStatusLoading(false);
      });
  }, [dataCollName, fetchTransformationStatus, updateStatus, isStaleStatus]);

  useEffect(() => {
    currentDataCollRef.current = dataCollName;
    triggeredAtRef.current = null; // Clear trigger timestamp when table changes
    updateStatus(null);
    setStatusLoading(false);
    stopPolling();
  }, [dataCollName, updateStatus, stopPolling]);

  useEffect(() => {
    triggeredAtRef.current = transformationTriggeredAt || null;
    // When a NEW transformation is triggered, clear old status immediately
    if (transformationTriggeredAt) {
      console.log('New transformation triggered, clearing old status');
      updateStatus(null);
    }
  }, [transformationTriggeredAt, updateStatus]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    transformationStatus,
    statusLoading,
    startPolling,
    stopPolling,
    manualRefresh,
  };
};
